import { useEffect, useState } from 'react';
import moment, { Moment } from 'moment';
import { useSessionStorage } from 'usehooks-ts';
import { DateFilters } from 'shared/components/table-filter-chips/models/date-filters';
import useTableFiltering, { TableFiltering } from 'shared/components/table/hooks/table-filtering';
import DateFormats from 'shared/constants/date-formats';
import FilterSessionStorageKeys from 'shared/constants/filterSessionStorageKeys';

import { ReadTemporaryAccessQuery } from '../domain/models/read-temporay-access-query';
import TemporaryAccess from '../domain/models/temporary-access';
import TemporaryAccessFilter from '../domain/models/temporary-access-filter';
import { FilterValueType } from '../domain/models/temporary-access-filter-value';

export interface ITemporaryAccessFilterHook {
  activeFilters: TemporaryAccessFilter[];
  searchBarValue: string;

  submitSearch: (query: string) => void;
  selectOption: (filterValueType: FilterValueType, keys: string[]) => void;
  clearFilters: () => void;
  dateFilter: DateFilters;
  setDateFilter: (dateFilter: DateFilters) => void;

  readTemporaryAccessQuery: ReadTemporaryAccessQuery;

  tableHook: TableFiltering<TemporaryAccess>;
}

const useTemporaryAccessFilters = (): ITemporaryAccessFilterHook => {
  const [activeFilters, setActiveFilters] = useState<TemporaryAccessFilter[]>([]);

  const defaultDateFilters = {
    dateFrom: null,
    dateTo: null,
  };

  const [dateFilterFromSessionStorage, setDateFilterFromSessionStorage] = useSessionStorage<DateFilters>(
    FilterSessionStorageKeys.TEMPORARY_ACCESS_DATE_FILTER,
    defaultDateFilters,
  );

  const tableHook = useTableFiltering<TemporaryAccess>();

  const [temporaryAccessFiltersFromSessionStorage, setTemporaryAccessFiltersFromSessionStorage] = useSessionStorage<
    TemporaryAccessFilter[]
  >(FilterSessionStorageKeys.TEMPORARY_ACCESS_FILTERS, []);

  const [searchBarValueFromSessionStorage, setSearchBarFromSessionStorage] = useSessionStorage<string>(
    FilterSessionStorageKeys.TEMPORARY_ACCESS_SEARCH_BAR,
    '',
  );

  const [readTemporaryAccessQuery, setReadTemporaryAccessQuery] = useState<ReadTemporaryAccessQuery>({
    filters: temporaryAccessFiltersFromSessionStorage,
    pagination: tableHook.currentPagination[0],
    searchQuery: searchBarValueFromSessionStorage,
    from: getDate(dateFilterFromSessionStorage.dateFrom),
    to: getDate(dateFilterFromSessionStorage.dateTo),
  } as ReadTemporaryAccessQuery);

  useEffect(() => {
    buildAndSaveQuery();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    tableHook.currentPage,
    tableHook.currentRowsPerPage,
    temporaryAccessFiltersFromSessionStorage,
    searchBarValueFromSessionStorage,
    dateFilterFromSessionStorage,
  ]);

  const submitSearch = (query: string) => {
    setSearchBarFromSessionStorage(query);
    tableHook.resetPaging();
  };

  const selectOption = (filterValueType: FilterValueType, keys: string[]) => {
    let updatedFiltersList = [...readTemporaryAccessQuery.filters];
    if (updatedFiltersList.findIndex((x) => x.filterValueType === filterValueType) !== -1) {
      updatedFiltersList[updatedFiltersList.findIndex((x) => x.filterValueType === filterValueType)] = {
        filterValueType: filterValueType,
        values: keys,
      };
    } else {
      updatedFiltersList.push({
        filterValueType: filterValueType,
        values: keys,
      });
    }

    updatedFiltersList = updatedFiltersList.filter((x) => x.values.length > 0);

    setTemporaryAccessFiltersFromSessionStorage(updatedFiltersList);

    tableHook.resetPaging();
  };

  const clearFilters = () => {
    setActiveFilters([]);
    setTemporaryAccessFiltersFromSessionStorage([]);
    setDateFilterFromSessionStorage(defaultDateFilters);
    setSearchBarFromSessionStorage('');
    setReadTemporaryAccessQuery((prevState) => ({
      ...prevState,
      filters: [],
    }));

    tableHook.resetPaging();
  };

  const setDateFilter = (dateFilter: DateFilters) => {
    setDateFilterFromSessionStorage(dateFilter);
    tableHook.resetPaging();
  };

  function getDate(date: Moment | null | undefined): Date | undefined {
    if (date) {
      return new Date(moment(date).format(DateFormats.ISO_8601_DATE_FORMAT));
    }
    return undefined;
  }

  function buildAndSaveQuery() {
    const from = getDate(dateFilterFromSessionStorage.dateFrom);
    const to = getDate(dateFilterFromSessionStorage.dateTo);

    setActiveFilters(temporaryAccessFiltersFromSessionStorage);
    setReadTemporaryAccessQuery((prevState) => ({
      ...prevState,
      pagination: tableHook.currentPagination[0],
      filters: temporaryAccessFiltersFromSessionStorage,
      searchQuery: searchBarValueFromSessionStorage,
      from: from,
      to: to,
    }));
  }

  return {
    activeFilters,
    searchBarValue: searchBarValueFromSessionStorage,

    selectOption,
    submitSearch,
    clearFilters,
    dateFilter: dateFilterFromSessionStorage,
    setDateFilter,

    readTemporaryAccessQuery,
    tableHook,
  };
};

export default useTemporaryAccessFilters;
